import React, { useEffect, useState } from 'react';
import { GhostGroup, GhostText, GhostBlock, GhostRows, GhostPara } from './Ghost';

/* Every waiting state in the 4sum console. House rule (UIKit/ghost/README.md): a screen
   waiting on data shows the SHAPE of that data - no spinner, no "Loading" string.

   The clubhouse leaderboard matters most here. It runs unattended on a screen people walk
   past, so a blank page with the word "Loading" on it is the whole display for however long
   the network takes; a board that is already the right shape reads as a board that is about
   to have numbers in it.

   NOT converted, deliberately: App.tsx:47 skips a loader on route chunks because "a spinner
   would flash for a chunk that loads in milliseconds". That is rule 6, already applied
   correctly. Leave it alone. */

/* Rule 6: under ~300ms, render nothing. Gated here so no screen can forget it. */
const GHOST_DELAY_MS = 300;

function Gate({ children }: { children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), GHOST_DELAY_MS);
    return () => clearTimeout(t);
  }, []);
  return show ? <>{children}</> : null;
}

/** Auth resolving from storage: the sign-in card's own outline, so nothing flashes. */
export function LoadingAuth() {
  return (
    <Gate>
      <GhostGroup label="Checking your session" className="wrap">
        <div className="card" style={{ maxWidth: 420 }}>
          <GhostText w="46%" />
          <div className="ghost-stack" style={{ marginTop: 14 }}>
            <GhostBlock h="40px" />
            <GhostBlock h="40px" />
          </div>
        </div>
      </GhostGroup>
    </Gate>
  );
}

/** A list of cards: accounts, events, contests. */
export function LoadingCards({ n = 3, label = 'Loading', wrap = false }: {
  n?: number; label?: string; wrap?: boolean;
}) {
  const body = (
    <GhostGroup label={label}>
      {Array.from({ length: n }, (_, i) => (
        <div className="card" style={{ marginBottom: 12 }} key={i}>
          <GhostText w={`${44 + ((i * 13) % 26)}%`} />
          <GhostText w={`${28 + ((i * 9) % 22)}%`} />
        </div>
      ))}
    </GhostGroup>
  );
  return <Gate>{wrap ? <div className="wrap">{body}</div> : body}</Gate>;
}

/** One event's page: title, progress strip, then the field. */
export function LoadingEvent() {
  return (
    <Gate>
      <GhostGroup label="Loading the event" className="wrap">
        <GhostText w="38%" />
        <div className="ghost-row" style={{ margin: '14px 0' }}>
          <GhostBlock h="12px" />
        </div>
        <div className="card">
          <GhostRows n={6} avatar={false} trailing />
        </div>
      </GhostGroup>
    </Gate>
  );
}

/** The clubhouse standings board. */
export function LoadingStandings() {
  return (
    <Gate>
      <GhostGroup label="Loading standings" className="wrap">
        <GhostText w="32%" />
        <div className="card" style={{ marginTop: 14 }}>
          <GhostRows n={10} avatar={false} trailing={6} />
        </div>
      </GhostGroup>
    </Gate>
  );
}

/** The scoring screen before a round is known. */
export function LoadingScore() {
  return (
    <Gate>
      <GhostGroup label="Loading the round" className="wrap sp">
        <GhostText w="42%" />
        <GhostPara lines={2} />
        <div className="card" style={{ marginTop: 14 }}>
          <GhostRows n={4} avatar={false} trailing />
        </div>
      </GhostGroup>
    </Gate>
  );
}
